/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    channel,
    onlyGroups,
    perPage,
    q,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        // Página grande en vista por sucursal (Victor 2026-06-08): la lista agrupada
        // necesita TODAS las conversaciones cargadas → con per_page alto se traen en
        // ~3 viajes en vez de ~22. El backend topa a 500.
        per_page: perPage,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        // Filtro por canal SERVER-SIDE (Victor 2026-06-06): el backend resuelve
        // 'whatsapp'|'instagram'|'facebook'|'tiktok' contra todas las bandejas.
        channel,
        // Pestaña Grupos (Victor 2026-06-06): trae TODOS los grupos de la cuenta.
        only_groups: onlyGroups,
        // Búsqueda global (Victor 2026-05-22): el `q` viaja al backend para que
        // filter_by_query busque por nombre/teléfono/email/contenido en TODOS los
        // estados (abiertas + cerradas). Sin esto el buscador no traía resueltas.
        q,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  toggleViewing({ conversationId, status }) {
    return axios.post(`${this.url}/${conversationId}/toggle_viewing`, { status });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  reclaim(conversationId, content) {
    return axios.post(
      `${this.url}/${conversationId}/reclaim`,
      content ? { content } : {}
    );
  }

  take(conversationId) {
    return axios.post(`${this.url}/${conversationId}/take`);
  }

  // Q1 · 2026-05-12 · M1.B · Tab Chats Urgentes cross-branch
  // Retorna TODAS las conversaciones abiertas del account sin filtrar por
  // inbox membership, branch activa ni PermissionFilterService. Para que la
  // vista de Urgentes muestre clientes de TODAS las sucursales.
  urgentIndex() {
    return axios.get(`${this.url}/urgent_index`);
  }

  // Leads Asignados cross-branch SCOPEADO (Req2 · Victor 2026-05-16):
  // super_admin = todas las sucursales; admin sucursal/GM = solo las suyas.
  assignedLeadsIndex() {
    return axios.get(`${this.url}/assigned_leads_index`);
  }

  // Tabs Selección / Retoque (Victor 2026-06-11): clientes de la sucursal activa
  // con lead en un embudo de Selección o de Retoque (cualquier etapa), separados
  // en dos listas. Para la encargada de Selección/Retoque.
  departmentIndex() {
    return axios.get(`${this.url}/department_index`);
  }

  meta({ inboxId, status, assigneeType, labels, teamId, conversationType }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
